import {
  dropPosition,
  pieceCells,
  type BoardState,
  type Link,
  type Piece,
  type Point,
} from '@bioforge/game-core';
import { BOSS_IDS, type BossId, type PublicBoss } from '@bioforge/protocol';
import type { CapsuleSide, JarCell, JarModel, JarSkin } from './jar-view.js';

/**
 * Картинка банки из состояния партии: поле, падающая капсула с местом приземления, босс
 * и отметки его способностей, туман. Принимает публичный вид босса — так её смогут строить
 * и сетевые экраны, где состояние приходит с сервера.
 */
export interface JarSource {
  readonly board: BoardState;
  readonly active: Piece | null;
  readonly boss: PublicBoss | null;
  readonly frost: boolean;
  /** Точка появления капсулы: штриховка, пока верхний ряд над ней пуст. */
  readonly spawn?: Point;
  readonly symbols?: boolean;
  readonly skin?: JarSkin | null;
}

/** Сторона половины капсулы по направлению на её пару. */
const SIDE: Readonly<Record<Link, CapsuleSide>> = {
  right: 'L',
  left: 'R',
  down: 'U',
  up: 'D',
  none: 'S',
};

function isBossId(id: string): id is BossId {
  return (BOSS_IDS as readonly string[]).includes(id);
}

/** Капсул до ближайшей способности босса (или до пробуждения), для кружка над ним. */
export function bossTimer(boss: PublicBoss): string {
  if (boss.dormant > 0) return String(boss.dormant);
  const countdowns = boss.abilities
    .map((a) => (a.done ? null : a.countdown))
    .filter((c): c is number => c !== null);
  return countdowns.length > 0 ? String(Math.min(...countdowns)) : '';
}

export function jarModelOf(source: JarSource): JarModel {
  const { board, active, boss } = source;
  const fog = boss?.fog ?? null;
  const fogged = (x: number) => fog !== null && x >= fog.x && x < fog.x + fog.width;

  const cells: JarCell[] = board.cells.map((cell, i): JarCell => {
    if (fogged(i % board.width)) return { kind: 'empty' };
    switch (cell.kind) {
      case 'virus':
        return {
          kind: 'microbe',
          color: cell.color,
          ...(cell.shield ? { armor: cell.guard ? ('gold' as const) : ('normal' as const) } : {}),
        };
      case 'segment':
        return { kind: 'piece', color: cell.color, side: SIDE[cell.link] };
      case 'wall':
        return { kind: 'wall' };
      case 'boss':
        // Тело рисуется одним рисунком поверх клеток; щупальца гибрида — по клеткам.
        return cell.part === undefined ? { kind: 'empty' } : { kind: 'part' };
      case 'empty':
        return { kind: 'empty' };
    }
  });
  const at = (x: number, y: number) =>
    x >= 0 && x < board.width && y >= 0 && y < board.height ? y * board.width + x : -1;

  // Отметки способностей: «+» на пустой клетке (рождение, отращивание), кольцо на микробе (мутация).
  for (const ability of boss?.abilities ?? []) {
    if (ability.kind === 'fog' || ability.done) continue;
    for (const target of ability.targets) {
      const i = at(target.x, target.y);
      const cell = cells[i];
      if (!cell || fogged(target.x)) continue;
      if (cell.kind === 'empty') cells[i] = { kind: 'spawn' };
      else if (cell.kind === 'microbe') cells[i] = { ...cell, mark: true };
    }
  }

  if (active) {
    for (const c of pieceCells(dropPosition(board, active))) {
      const i = at(c.x, c.y);
      if (cells[i]?.kind === 'empty') cells[i] = { kind: 'ghost', side: SIDE[c.link] };
    }
    for (const c of pieceCells(active)) {
      const i = at(c.x, c.y);
      if (i >= 0) cells[i] = { kind: 'piece', color: c.color, side: SIDE[c.link], active: true };
    }
  }

  const spawn = source.spawn;
  const spawnColumns =
    spawn && [spawn.x, spawn.x + 1].every((x) => cells[at(x, 0)]?.kind === 'empty')
      ? [spawn.x, spawn.x + 1]
      : [];

  return {
    width: board.width,
    height: board.height,
    cells,
    boss:
      boss?.body && isBossId(boss.id)
        ? { ...boss.body, id: boss.id, timer: bossTimer(boss) }
        : null,
    spawnColumns,
    fog: fog ? { x: fog.x, width: fog.width } : null,
    frost: source.frost,
    symbols: source.symbols ?? true,
    skin: source.skin ?? null,
  };
}
